import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/errors';
import { useErrorMessage } from './use-error-message';

/**
 * Puts a VALIDATION_ERROR's issues on the form's own fields, translated by issue code.
 *
 * `fields` are the request paths the form shows. An issue lands on the longest
 * field that is its path or a prefix of it (`overrides.0.permission` on
 * `overrides`). `fail(error)` records the issues and returns the form-level
 * message: "check the highlighted fields" only when an issue actually landed on
 * a field, otherwise the error's own message (so it never points at nothing).
 */
export function useFieldIssues(fields: readonly string[]) {
  const { t, i18n } = useTranslation();
  const errorMessage = useErrorMessage();
  const [issues, setIssues] = useState<Record<string, string>>({});

  const fail = (error: unknown): string => {
    const landed: Record<string, string> = {};
    if (error instanceof ApiError) {
      for (const issue of error.issues) {
        const field = fields
          .filter((name) => issue.path === name || issue.path.startsWith(`${name}.`))
          .sort((a, b) => b.length - a.length)[0];
        if (!field || landed[field]) continue;
        const key = `errors.issue.${issue.code}`;
        landed[field] = i18n.exists(key) ? t(key) : t('errors.issue.default');
      }
    }
    setIssues(landed);
    return Object.keys(landed).length > 0 ? t('errors.checkFields') : errorMessage(error);
  };

  return { issues, fail, clear: () => setIssues({}) };
}
